import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Upload, X, Image, Link } from "lucide-react";

function ImagePreview({ src }) {
  const [error, setError] = useState(false);
  if (error) {
    return (
      <div className="h-32 rounded-lg overflow-hidden bg-[#27272a] flex items-center justify-center text-zinc-500 text-sm">
        Immagine non valida
      </div>
    );
  }
  return (
    <div className="h-32 rounded-lg overflow-hidden bg-[#27272a]">
      <img src={src} alt="Preview" className="w-full h-full object-cover" onError={() => setError(true)} />
    </div>
  );
}

export default function ImageUploader({ value, onChange, authAxios }) {
  const [uploading, setUploading] = useState(false);
  const [useUrl, setUseUrl] = useState(!value || value.startsWith("http"));
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!validTypes.includes(file.type)) {
      toast.error("Formato non supportato. Usa JPG, PNG, WEBP o GIF");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File troppo grande (max 5MB)");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await authAxios.post("/upload/image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Get the URL for the uploaded image (now cloud-based)
      const imageUrl = response.data.url;
      onChange(imageUrl);
      toast.success("Immagine caricata");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il caricamento");
    } finally {
      setUploading(false);
    }
  };

  const handleUrlChange = (e) => {
    onChange(e.target.value);
  };

  const clearImage = () => {
    onChange("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3">
      {/* Toggle between upload and URL */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant={!useUrl ? "default" : "outline"}
          size="sm"
          onClick={() => setUseUrl(false)}
          className={!useUrl ? "bg-amber-500 text-black" : "border-white/10"}
        >
          <Upload className="w-4 h-4 mr-1" />
          Carica
        </Button>
        <Button
          type="button"
          variant={useUrl ? "default" : "outline"}
          size="sm"
          onClick={() => setUseUrl(true)}
          className={useUrl ? "bg-amber-500 text-black" : "border-white/10"}
        >
          <Link className="w-4 h-4 mr-1" />
          URL
        </Button>
      </div>

      {useUrl ? (
        /* URL Input */
        <Input
          type="url"
          value={value || ""}
          onChange={handleUrlChange}
          placeholder="https://esempio.com/immagine.jpg"
          className="bg-[#27272a] border-white/10"
        />
      ) : (
        /* File Upload */
        <div className="space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleFileChange}
            className="hidden"
            id="image-upload"
          />
          <label
            htmlFor="image-upload"
            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
              uploading
                ? "border-amber-500/50 bg-amber-500/10"
                : "border-white/10 hover:border-amber-500/50 hover:bg-white/5"
            }`}
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-amber-500 border-t-transparent"></div>
                <span className="text-zinc-400">Caricamento...</span>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 text-zinc-400" />
                <span className="text-zinc-400">Clicca per caricare (max 5MB)</span>
              </>
            )}
          </label>
        </div>
      )}

      {/* Image Preview */}
      {value && (
        <div className="relative group">
          <ImagePreview src={value} />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            onClick={clearImage}
            className="absolute top-2 right-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
